export type EquipmentCatalogueIdentity = { sku: string; productId: string };

export type EquipmentListIssueCode =
  | 'BLANK_SKU'
  | 'MALFORMED_CSV'
  | 'INVALID_COLUMN_COUNT'
  | 'INVALID_QUANTITY'
  | 'DUPLICATE_SKU'
  | 'UNMATCHED_CATALOGUE_IDENTITY'
  | 'CATALOGUE_IDENTITY_AMBIGUOUS';

export type EquipmentListRowIssue = { code: EquipmentListIssueCode; message: string };
export type EquipmentListRow = {
  rowNumber: number;
  raw: string;
  source: { sku: string; description: string; quantity: string; uom: string; extraFields: string[] };
  catalogueProductId: string | null;
  issues: EquipmentListRowIssue[];
};

export type EquipmentListGlobalIssue = 'INPUT_TOO_LARGE' | 'ROW_LIMIT_EXCEEDED' | 'REQUEST_SPLIT_REQUIRED' | 'NO_DATA_ROWS';
export type EquipmentListOverflow = {
  firstRowNumber: number;
  rowCount: number | null;
  rawText: string;
  splitSize: number;
};
export type EquipmentListParseResult = {
  delimiter: ',' | '\t';
  rows: EquipmentListRow[];
  issues: EquipmentListGlobalIssue[];
  overflow: EquipmentListOverflow | null;
  inputBytes: number;
  maxRows: number;
  maxColumns: number;
  maxLinesPerRequest: number;
};

export type EquipmentListParserOptions = {
  catalogue?: readonly EquipmentCatalogueIdentity[];
  maxBytes?: number;
  maxRows?: number;
  maxColumns?: number;
  maxLinesPerRequest?: number;
};

type CsvRecord = { fields: string[]; raw: string; malformed: boolean; columnOverflow: boolean; start: number; end: number };
const byteLength = (value: string) => new TextEncoder().encode(value).byteLength;
const normalizeSku = (value: string) => value.trim().replace(/\s+/g, ' ').toLocaleUpperCase('en-GB');

/** Detects comma CSV or pasted tab-separated spreadsheet rows from the first record. */
function detectDelimiter(input: string): ',' | '\t' {
  let quoted = false;
  let commas = 0;
  let tabs = 0;
  for (let index = 0; index < input.length; index++) {
    const char = input[index];
    if (char === '"') {
      if (quoted && input[index + 1] === '"') index++;
      else quoted = !quoted;
    } else if (!quoted && (char === '\n' || char === '\r')) break;
    else if (!quoted && char === ',') commas++;
    else if (!quoted && char === '\t') tabs++;
  }
  return tabs > commas ? '\t' : ',';
}

/** CSV lexer retaining each exact record so malformed rows are never silently discarded. */
function tokenize(input: string, delimiter: ',' | '\t', maxColumns: number): CsvRecord[] {
  const records: CsvRecord[] = [];
  let fields: string[] = [];
  let field = '';
  let quoted = false;
  let afterQuote = false;
  let malformed = false;
  let columnOverflow = false;
  let recordStart = 0;
  const pushField = () => {
    if (fields.length < maxColumns) fields.push(field);
    else columnOverflow = true;
    field = '';
    afterQuote = false;
  };
  const pushRecord = (end: number, nextStart: number) => {
    pushField();
    records.push({ fields, raw: input.slice(recordStart, end), malformed, columnOverflow, start: recordStart, end });
    fields = [];
    field = '';
    quoted = false;
    afterQuote = false;
    malformed = false;
    columnOverflow = false;
    recordStart = nextStart;
  };

  for (let index = 0; index < input.length; index++) {
    const char = input[index];
    if (quoted) {
      if (char === '"') {
        if (input[index + 1] === '"') { field += '"'; index++; }
        else { quoted = false; afterQuote = true; }
      } else field += char;
      continue;
    }
    if (afterQuote) {
      if (char === delimiter) { pushField(); continue; }
      if (char === '\r' || char === '\n') {
        const nextStart = char === '\r' && input[index + 1] === '\n' ? index + 2 : index + 1;
        if (nextStart > index + 1) index++;
        pushRecord(index + 1, nextStart);
        continue;
      }
      if (char === ' ' || char === '\t') continue;
      malformed = true;
      field += char;
      afterQuote = false;
      continue;
    }
    if (char === '"') {
      if (field.length === 0) quoted = true;
      else { malformed = true; field += char; }
    } else if (char === delimiter) pushField();
    else if (char === '\r' || char === '\n') {
      const nextStart = char === '\r' && input[index + 1] === '\n' ? index + 2 : index + 1;
      if (nextStart > index + 1) index++;
      pushRecord(index + 1, nextStart);
    } else field += char;
  }
  if (recordStart < input.length || field.length > 0 || fields.length > 0 || quoted) {
    if (quoted) malformed = true;
    pushRecord(input.length, input.length);
  }
  return records;
}

function isHeader(fields: string[]) {
  const first = normalizeSku(fields[0] ?? '').replace(/[^A-Z0-9]/g, '');
  return ['SKU', 'PARTNUMBER', 'PRODUCTCODE', 'OWNCODE'].includes(first);
}

function rowIssue(code: EquipmentListIssueCode): EquipmentListRowIssue {
  const messages: Record<EquipmentListIssueCode, string> = {
    BLANK_SKU: 'Add a SKU or product code.',
    MALFORMED_CSV: 'This row has malformed CSV quoting.',
    INVALID_COLUMN_COUNT: 'Use SKU, description, quantity and optional unit columns.',
    INVALID_QUANTITY: 'Enter a positive quantity using digits and an optional decimal point.',
    DUPLICATE_SKU: 'This SKU appears more than once; rows were kept separate.',
    UNMATCHED_CATALOGUE_IDENTITY: 'This SKU does not match the supplied catalogue identities.',
    CATALOGUE_IDENTITY_AMBIGUOUS: 'This SKU matches more than one supplied catalogue identity.',
  };
  return { code, message: messages[code] };
}

/**
 * Parses bounded comma CSV or pasted TSV without resolving names heuristically.
 * Product IDs are attached only when an exact normalized SKU match is supplied by the caller.
 */
export function parseEquipmentList(input: string, options: EquipmentListParserOptions = {}): EquipmentListParseResult {
  const maxBytes = options.maxBytes ?? 64 * 1024;
  const maxRows = options.maxRows ?? 500;
  if (!Number.isSafeInteger(maxRows) || maxRows < 1 || maxRows > 500) throw new Error('maxRows must be 1–500');
  const maxLinesPerRequest = options.maxLinesPerRequest ?? 50;
  if (!Number.isSafeInteger(maxLinesPerRequest) || maxLinesPerRequest < 1 || maxLinesPerRequest > 500) throw new Error('maxLinesPerRequest must be 1–500');
  const maxColumns = options.maxColumns ?? 16;
  const delimiter = detectDelimiter(input);
  const inputBytes = byteLength(input);
  const result = (rows: EquipmentListRow[], issues: EquipmentListGlobalIssue[], overflow: EquipmentListOverflow | null): EquipmentListParseResult => ({ delimiter, rows, issues, overflow, inputBytes, maxRows, maxColumns, maxLinesPerRequest });
  if (inputBytes > maxBytes) return result([], ['INPUT_TOO_LARGE'], null);
  const records = tokenize(input, delimiter, maxColumns);
  const nonemptyRecords = records.filter((record) => record.fields.some((field) => field.trim().length > 0));
  let start = 0;
  let headers: string[] | undefined;
  const headerRecord = nonemptyRecords[0];
  if (headerRecord && isHeader(headerRecord.fields)) {
    headers = headerRecord.fields.map((field) => normalizeSku(field).replace(/[^A-Z0-9]/g, ''));
    start = records.indexOf(headerRecord) + 1;
  }
  const dataRecords = records.slice(start).filter((record) => record.fields.some((field) => field.trim().length > 0));
  const rows: EquipmentListRow[] = [];
  const issues: EquipmentListGlobalIssue[] = [];
  let overflow: EquipmentListOverflow | null = null;
  const structuredRecords = dataRecords.slice(0, maxRows);
  const firstOverflowRecord = dataRecords[maxRows];
  if (firstOverflowRecord) {
    const firstIndex = records.indexOf(firstOverflowRecord);
    const rawText = input.slice(firstOverflowRecord.start);
    const rowCount = dataRecords.length - maxRows;
    overflow = { firstRowNumber: firstIndex + 1, rowCount, rawText, splitSize: maxLinesPerRequest };
    issues.push('ROW_LIMIT_EXCEEDED');
  }

  const catalogue = new Map<string, string[]>();
  for (const entry of options.catalogue ?? []) {
    const sku = normalizeSku(entry.sku);
    if (!sku || !entry.productId.trim()) continue;
    catalogue.set(sku, [...(catalogue.get(sku) ?? []), entry.productId]);
  }
  const occurrences = new Map<string, number>();
  const columnIndex = (name: string, fallback: number) => headers?.findIndex((header) => [name, ...(name === 'SKU' ? ['PARTNUMBER', 'PRODUCTCODE', 'OWNCODE'] : name === 'QUANTITY' ? ['QTY'] : name === 'UOM' ? ['UNIT'] : [])].includes(header)) ?? fallback;
  for (const record of structuredRecords) {
    const get = (name: string, fallback: number) => record.fields[columnIndex(name, fallback)] ?? '';
    const sku = get('SKU', 0).trim();
    const description = get('DESCRIPTION', 1).trim();
    const quantity = get('QUANTITY', 2).trim();
    const uom = get('UOM', 3).trim();
    const sourceIndices = new Set([columnIndex('SKU', 0), columnIndex('DESCRIPTION', 1), columnIndex('QUANTITY', 2), columnIndex('UOM', 3)]);
    const extraFields = record.fields.filter((_, index) => !sourceIndices.has(index));
    const rowIssues: EquipmentListRowIssue[] = [];
    if (record.malformed) rowIssues.push(rowIssue('MALFORMED_CSV'));
    if (record.columnOverflow || record.fields.length < 3 || record.fields.length > 4) rowIssues.push(rowIssue('INVALID_COLUMN_COUNT'));
    if (!sku) rowIssues.push(rowIssue('BLANK_SKU'));
    if (!/^(?:0|[1-9]\d{0,3})(?:\.\d{1,3})?$/.test(quantity) || Number(quantity) <= 0 || Number(quantity) > 9999) rowIssues.push(rowIssue('INVALID_QUANTITY'));
    let catalogueProductId: string | null = null;
    if (sku) {
      const normalized = normalizeSku(sku);
      const count = (occurrences.get(normalized) ?? 0) + 1;
      occurrences.set(normalized, count);
      if (count > 1) rowIssues.push(rowIssue('DUPLICATE_SKU'));
      const matches = catalogue.get(normalized) ?? [];
      if (matches.length === 1) catalogueProductId = matches[0] ?? null;
      else if (matches.length === 0) rowIssues.push(rowIssue('UNMATCHED_CATALOGUE_IDENTITY'));
      else rowIssues.push(rowIssue('CATALOGUE_IDENTITY_AMBIGUOUS'));
    }
    rows.push({ rowNumber: records.indexOf(record) + 1, raw: record.raw, source: { sku, description, quantity, uom, extraFields }, catalogueProductId, issues: rowIssues });
  }
  if (rows.length > maxLinesPerRequest || overflow) issues.push('REQUEST_SPLIT_REQUIRED');
  if (rows.length === 0 && !overflow) issues.push('NO_DATA_ROWS');
  return result(rows, issues, overflow);
}
